import type { PaginatedResponse } from "@/features/cle-compliance/models/courseCertificate";
import type {
  AuthorshipEvidenceListModel,
  AuthorshipEvidenceModel,
  AuthorshipQueryParams,
} from "@/features/cle-compliance/models/authorship";
import { del, get, post } from "./util";

export const AUTHORSHIPS_QUERY_KEY = ["authorships"] as const;

export const AUTHORSHIPS_LIST_QUERY_KEY = [
  ...AUTHORSHIPS_QUERY_KEY,
  "list",
] as const;

export const createAuthorshipDetailQueryKey = (id: string) =>
  [...AUTHORSHIPS_QUERY_KEY, "detail", id] as const;

const AUTHORSHIPS_QUERY = "authorships";

const createAuthorshipsForReportingQuery = (reportId: string): string =>
  `${AUTHORSHIPS_QUERY}/available-for-period/${reportId}`;

const createAuthorshipsForInjunctionQuery = (injunctionId: string): string =>
  `${AUTHORSHIPS_QUERY}/available-for-injunction/${injunctionId}`;

const createAuthorshipsForImpositionQuery = (impositionId: string): string =>
  `${AUTHORSHIPS_QUERY}/available-for-imposition/${impositionId}`;

const createSearchParams = ({
  page,
  page_size,
  reported,
}: AuthorshipQueryParams): string => {
  const searchParams = new URLSearchParams();

  if (reported != null) {
    searchParams.set("reported", String(reported));
  }

  if (page != null) {
    searchParams.set("page", String(page));
  }

  if (page_size != null) {
    searchParams.set("page_size", String(page_size));
  }

  return searchParams.toString();
};

const createListQuery = (params: AuthorshipQueryParams = {}): string => {
  const queryString = createSearchParams(params);

  return queryString
    ? `${AUTHORSHIPS_QUERY}?${queryString}`
    : AUTHORSHIPS_QUERY;
};

export type CreateAuthorshipRequest = {
  bookTitle: string;
  publisher: string;
  publicationYear: number;
  numberOfLessons: number;
  isbn?: string | null;
};

export const fetchAuthorships = (
  params: AuthorshipQueryParams = {},
): Promise<PaginatedResponse<AuthorshipEvidenceListModel>> =>
  get<PaginatedResponse<AuthorshipEvidenceListModel>>({
    query: createListQuery(params),
  });

export const fetchAuthorshipsForReporting = (
  reportId: string,
): Promise<AuthorshipEvidenceListModel[]> =>
  get<AuthorshipEvidenceListModel[]>({
    query: createAuthorshipsForReportingQuery(reportId),
  });

export const fetchAuthorshipsForInjunction = (
  injunctionId: string,
): Promise<AuthorshipEvidenceListModel[]> =>
  get<AuthorshipEvidenceListModel[]>({
    query: createAuthorshipsForInjunctionQuery(injunctionId),
  });

export const fetchAuthorshipsForImposition = (
  impositionId: string,
): Promise<AuthorshipEvidenceListModel[]> =>
  get<AuthorshipEvidenceListModel[]>({
    query: createAuthorshipsForImpositionQuery(impositionId),
  });

export const fetchAuthorshipById = (
  id: string,
): Promise<AuthorshipEvidenceModel> =>
  get<AuthorshipEvidenceModel>({
    query: `${AUTHORSHIPS_QUERY}/${id}`,
  });

export const createAuthorship = (
  request: CreateAuthorshipRequest,
): Promise<AuthorshipEvidenceModel> =>
  post<AuthorshipEvidenceModel>({
    query: AUTHORSHIPS_QUERY,
    body: request,
  });

export const deleteAuthorship = async (id: string): Promise<void> => {
  await del({
    query: `${AUTHORSHIPS_QUERY}/${id}`,
  });
};
